"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  if (loading) {
    return <>
        <div className="hidden md:flex gap-3">
          <div className="h-10 w-24 bg-red-100/40 animate-pulse rounded-lg" />
          <div className="h-10 w-24 bg-red-100/40 animate-pulse rounded-lg" />
          <div className="h-10 w-24 bg-red-100/40 animate-pulse rounded-lg" />
          <div className="h-10 w-24 bg-red-100/40 animate-pulse rounded-lg" />
        </div>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Menu">
          <Menu className="size-6" />
        </Button>
      </>;
  }
  return <>
      {}
      <div className="hidden md:flex items-center gap-10">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-1">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="relative px-6 py-3 text-[15px] font-semibold tracking-wide text-foreground/90 transition-all duration-300 ease-[var(--ease-smooth)] hover:!bg-transparent focus:!bg-transparent hover:text-red-500 hover:scale-105 group">
                    {item.label}
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-red-500 via-red-400 to-red-600 transition-all duration-300 ease-[var(--ease-spring)] group-hover:w-[80%]" />
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>

        {}
        <div className="relative">
          {searchOpen ? <Input type="search" placeholder="Search..." className="w-64 h-11 transition-all duration-300 ease-[var(--ease-spring)] border-red-500/40 focus-visible:border-red-500 bg-light-background/80 backdrop-blur-sm shadow-soft" autoFocus onBlur={() => setSearchOpen(false)} /> : <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="hover:bg-red-500 hover:text-white transition-all duration-300 hover:scale-110 rounded-lg" aria-label="Search">
              <Search className="size-5" />
            </Button>}
        </div>
      </div>

      {}
      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden hover:bg-red-500/20 transition-all duration-300 hover:scale-110 rounded-lg" aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {}
      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-light-background/98 backdrop-blur-xl border-b border-red-500/20 shadow-strong md:hidden animate-in fade-in-0 slide-in-from-top-5 duration-300">
          <nav className="flex flex-col p-8 gap-2 max-w-lg mx-auto">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-6 py-4 text-base font-semibold text-foreground/90 hover:bg-red-500 hover:text-white rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-soft" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
            <div className="mt-4 pt-4 border-t border-border/30">
              <Input type="search" placeholder="Search..." className="w-full h-12 bg-light-background/80 backdrop-blur-sm border-red-500/30 shadow-soft focus-visible:border-red-500" />
            </div>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-light-background/96 backdrop-blur-xl border-b transition-all duration-500 ease-[var(--ease-smooth)] ${scrolled ? "h-20 shadow-strong border-red-500/30 bg-light-background/98" : "h-24 border-border/20 shadow-soft"}`}>
      <div className="container mx-auto px-6 lg:px-8 h-full flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 transition-all duration-300 hover:opacity-90 hover:scale-105">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521681/img/aa_eagle_plumbing_logo.png" alt="AA Eagle Plumbing" className="h-7" />
        </Link>

        {}
        <MenuProvider menu_id="3">
          <HeaderMenuConsumer />
        </MenuProvider>
      </div>
    </header>;
}